import React from "react";

export interface EmployeeStats {
  total: number;
  active: number;
  departments: number;
  newThisMonth: number;
}

export type EmployeePillType = "all" | "active" | "departments" | "new_this_month";

interface StatPill {
  id: EmployeePillType;
  label: string;
  count: number;
  dot?: string;
}

interface EmployeeStatsCardsProps {
  stats: EmployeeStats;
  activePill?: EmployeePillType | "";
  onSelectPill?: (pill: EmployeePillType) => void;
}

const EmployeeStatsCards: React.FC<EmployeeStatsCardsProps> = ({
  stats,
  activePill = "all",
  onSelectPill,
}) => {
  const pills: StatPill[] = [
    { id: "all", label: "All", count: stats.total },
    { id: "active", label: "Active", count: stats.active, dot: "var(--color-success)" },
    { id: "departments", label: "Departments", count: stats.departments, dot: "var(--color-primary-600)" },
    { id: "new_this_month", label: "New This Month", count: stats.newThisMonth, dot: "var(--color-warning)" },
  ];

  return (
    <div className="flex gap-[10px] flex-wrap" role="tablist" aria-label="Employee Quick Filters">
      {pills.map((pill) => {
        const isActive = activePill === pill.id;
        return (
          <button
            key={pill.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelectPill?.(pill.id)}
            className={`flex items-center gap-[9px] h-10 px-[15px] rounded-[11px] transition-all duration-150 border cursor-pointer select-none outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-600)] ${
              isActive
                ? "border-[var(--color-primary-600)] bg-[var(--color-primary-50)] shadow-xs"
                : "border-[var(--color-border-primary)] bg-white hover:border-[var(--color-border-hover)] hover:bg-[var(--color-bg-subtle)]"
            }`}
          >
            {pill.dot && (
              <span
                className="w-[7px] h-[7px] rounded-full flex-none transition-transform duration-150"
                style={{ background: pill.dot }}
              />
            )}
            <span
              className={`text-body-base font-semibold ${
                isActive
                  ? "text-[var(--color-primary-600)]"
                  : "text-[var(--color-text-secondary)]"
              }`}
            >
              {pill.label}
            </span>
            <span
              className={`text-body-base font-extrabold ${
                isActive
                  ? "text-[var(--color-primary-600)]"
                  : "text-[var(--color-text-primary)]"
              }`}
            >
              {pill.count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default EmployeeStatsCards;
